import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../theme';

const API_BASE = 'https://filtrd-prod-f09ee9687166.herokuapp.com/api';
const STORAGE_KEY = 'admin_api_key';

interface OnboardingFormData {
  email?: string;
  firstName?: string;
  topics?: string;
  topicsCustom?: string;
  accounts?: string;
  accountsCustom?: string;
  useCase?: string;
  personalGoals?: string;
  businessGoals?: string;
  businessName?: string;
  digestFrequency?: number | string;
}

interface Onboarding {
  id: string;
  email: string;
  formData: OnboardingFormData;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

const AdminOnboardingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [onboardings, setOnboardings] = useState<Onboarding[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchOnboardings = useCallback(async () => {
    if (!apiKey) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/onboardings`, {
        headers: {
          'X-API-Key': apiKey,
          'X-User-Id': 'admin',
          'X-App-Name': 'Filtrd',
          'X-Permissions': 'onboardings:read',
        },
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Invalid API key');
        }
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }

      const data = await response.json();
      // API returns { success: true, data: { items: [...] } }
      const items = data?.data?.items || data?.items || data?.data || [];
      setOnboardings(Array.isArray(items) ? items : []);
      setIsAuthenticated(true);
      localStorage.setItem(STORAGE_KEY, apiKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch onboardings');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    setDeletingId(id);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/onboardings/${id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': apiKey,
          'X-User-Id': 'admin',
          'X-App-Name': 'Filtrd',
          'X-Permissions': 'onboardings:delete',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete: ${response.statusText}`);
      }

      setOnboardings((prev) => prev.filter((o) => o.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    fetchOnboardings();
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setOnboardings([]);
    localStorage.removeItem(STORAGE_KEY);
    setApiKey('');
  };

  useEffect(() => {
    if (apiKey && !isAuthenticated) {
      fetchOnboardings();
    }
  }, [apiKey, isAuthenticated, fetchOnboardings]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatValue = (val?: string | string[]) => {
    if (!val) return '-';
    if (Array.isArray(val)) return val.join(', ') || '-';
    return val || '-';
  };

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: colors.deepNavy,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            sx={{
              p: 4,
              backgroundColor: colors.whiteAlpha[10],
              border: colors.borders.whiteAlpha10,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: colors.pureWhite,
                mb: 3,
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              Admin Login
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="API Key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    color: colors.pureWhite,
                    '& fieldset': {
                      borderColor: colors.whiteAlpha[20],
                    },
                    '&:hover fieldset': {
                      borderColor: colors.whiteAlpha[30],
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: colors.signalOrange,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: colors.slate[400],
                    '&.Mui-focused': {
                      color: colors.signalOrange,
                    },
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !apiKey}
                sx={{
                  backgroundColor: colors.signalOrange,
                  '&:hover': {
                    backgroundColor: colors.signalOrangeHover,
                  },
                  '&:disabled': {
                    backgroundColor: colors.slate[600],
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: colors.deepNavy,
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: colors.pureWhite,
              fontWeight: 700,
            }}
          >
            Onboardings Admin
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={() => navigate('/admin/users')}
              sx={{
                color: colors.signalOrange,
                borderColor: colors.signalOrange,
                '&:hover': {
                  borderColor: colors.signalOrangeHover,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Users
            </Button>
            <Button
              onClick={() => navigate('/admin/x-users')}
              sx={{
                color: colors.signalOrange,
                borderColor: colors.signalOrange,
                '&:hover': {
                  borderColor: colors.signalOrangeHover,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              X Users
            </Button>
            <Button
              onClick={() => navigate('/admin/categories')}
              sx={{
                color: colors.signalOrange,
                borderColor: colors.signalOrange,
                '&:hover': {
                  borderColor: colors.signalOrangeHover,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Categories
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchOnboardings}
              disabled={loading}
              sx={{
                color: colors.pureWhite,
                borderColor: colors.whiteAlpha[30],
                '&:hover': {
                  borderColor: colors.pureWhite,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              sx={{
                color: colors.error,
                borderColor: colors.error,
                '&:hover': {
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Logout
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Stats */}
        <Typography sx={{ mb: 3, color: colors.slate[400] }}>
          Total: {onboardings.length} entries
        </Typography>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: colors.signalOrange }} />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: colors.whiteAlpha[10],
              border: colors.borders.whiteAlpha10,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Topics</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Accounts</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Use Case</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Goals</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Frequency</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Created</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {onboardings.map((onboarding) => (
                  <TableRow
                    key={onboarding.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: colors.whiteAlpha[10],
                      },
                    }}
                  >
                    <TableCell sx={{ color: colors.pureWhite, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {onboarding.email}
                    </TableCell>
                    <TableCell sx={{ color: colors.pureWhite }}>
                      {onboarding.formData?.firstName || '-'}
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400], maxWidth: 150 }}>
                      {formatValue(onboarding.formData?.topics)}
                      {onboarding.formData?.topicsCustom && ` (${onboarding.formData.topicsCustom})`}
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400], maxWidth: 150 }}>
                      {formatValue(onboarding.formData?.accounts)}
                      {onboarding.formData?.accountsCustom && ` (${onboarding.formData.accountsCustom})`}
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400] }}>
                      {onboarding.formData?.useCase || '-'}
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400], maxWidth: 150 }}>
                      {onboarding.formData?.useCase === 'personal'
                        ? formatValue(onboarding.formData?.personalGoals)
                        : onboarding.formData?.useCase === 'business'
                        ? formatValue(onboarding.formData?.businessGoals)
                        : `P: ${formatValue(onboarding.formData?.personalGoals)} / B: ${formatValue(onboarding.formData?.businessGoals)}`}
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400] }}>
                      {onboarding.formData?.digestFrequency ?? '-'}
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400], fontSize: '0.75rem' }}>
                      {formatDate(onboarding.createdAt)}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleDelete(onboarding.id)}
                        disabled={deletingId === onboarding.id}
                        sx={{
                          color: colors.error,
                          '&:hover': {
                            backgroundColor: colors.whiteAlpha[10],
                          },
                        }}
                      >
                        {deletingId === onboarding.id ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {onboardings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ color: colors.slate[400], textAlign: 'center', py: 4 }}>
                      No onboardings found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Box>
  );
};

export default AdminOnboardingsPage;
